import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Linkedin, Coffee, ArrowUpRight, Loader2, Send } from "lucide-react";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "8258db55-5678-441b-8830-24387cc4d93f";

const CONTACT = {
  email: "kontakt@vaskopeter.sk",
  phone: "+421 908 898 093",
  linkedin: "https://linkedin.com/in/petervasko1/",
  linkedinLabel: "linkedin.com/in/petervasko1/",
};

export default function Contact() {
  // `botcheck` is the Web3Forms native honeypot — must remain empty for real users
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", botcheck: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Vyplň prosím meno, e-mail a správu.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        from_name: form.name.trim(),
        subject:
          form.subject.trim() ||
          `Nová správa z portfólia od ${form.name.trim()}`,
        replyto: form.email.trim(),
        botcheck: form.botcheck,
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      };

      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.clone().json();

      if (res.ok && data.success) {
        toast.success("Správa odoslaná. Ozvem sa do 24 hodín.");
        setForm({ name: "", email: "", subject: "", message: "", botcheck: "" });
      } else {
        throw new Error(data.message || "Odoslanie zlyhalo.");
      }
    } catch (err) {
      toast.error(err?.message || "Niečo sa pokazilo. Skús to prosím znova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 border-t border-white/[0.06]"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* LEFT */}
          <div className="lg:col-span-5">
            <span className="pill-tag" data-testid="contact-pill">
              <span className="pill-dot"></span>
              06 — Kontakt
            </span>
            <h2 className="mt-6 text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98]">
              Čas na prvý <br />
              <span className="text-[#C6F432]">krok</span>
            </h2>
            <p className="mt-6 text-lg text-white/65 max-w-md leading-relaxed">
              Hľadáte skúseného lídra do vášho tímu? Rád s vami preberiem vaše marketingové ciele — osobne v Trnave alebo online.
            </p>

            <div className="mt-10 space-y-3">
              <ContactRow
                testId="contact-link-email"
                href={`mailto:${CONTACT.email}`}
                icon={Mail}
                label="E-mail"
                value={CONTACT.email}
              />
              <ContactRow
                testId="contact-link-phone"
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                icon={Phone}
                label="Telefón"
                value={CONTACT.phone}
              />
              <ContactRow
                testId="contact-link-linkedin"
                href={CONTACT.linkedin}
                icon={Linkedin}
                label="LinkedIn"
                value={CONTACT.linkedinLabel}
                external
              />
            </div>

            <a
              data-testid="contact-coffee-cta"
              href={`mailto:${CONTACT.email}?subject=Stretnutie%20%2F%20Online%20call`}
              className="mt-10 inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-white/[0.04] border border-white/10 hover:bg-[#C6F432] hover:text-[#0A0A0A] hover:border-[#C6F432] transition-all text-sm font-semibold"
            >
              <Coffee className="w-4 h-4" />
              Stretnutie / online call
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              noValidate
              className="relative p-7 lg:p-10 rounded-3xl border border-white/[0.07] bg-gradient-to-br from-white/[0.03] to-white/[0.005]"
            >
              <div className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#C6F432]/50 to-transparent" />

              {/* Honeypot — Web3Forms native `botcheck` field. Off-screen, not focusable. Real users won't touch it. */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-10000px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Website (nevyplňať)
                  <input
                    type="checkbox"
                    name="botcheck"
                    tabIndex={-1}
                    autoComplete="off"
                    checked={!!form.botcheck}
                    onChange={(e) => setForm((f) => ({ ...f, botcheck: e.target.checked ? "1" : "" }))}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Meno *">
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    type="text"
                    required
                    placeholder="Vaše meno"
                    className="input"
                  />
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    type="email"
                    required
                    placeholder="vy@firma.sk"
                    className="input"
                  />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Predmet">
                  <input
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    type="text"
                    placeholder="O čo ide?"
                    className="input"
                  />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Správa *">
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={6}
                    placeholder="Pár viet o projekte..."
                    className="input resize-none"
                  />
                </Field>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-white/40 max-w-sm">
                  Odoslaním súhlasíte so spracovaním e-mailu výlučne na účely
                  odpovede na tvoju správu.
                </p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C6F432] text-[#0A0A0A] text-sm font-semibold hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors btn-shine"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? "Odosielam..." : "Odoslať správu"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <footer data-testid="footer" className="mt-24 pt-10 border-t border-white/[0.06]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-[#C6F432] text-[#0A0A0A] font-black flex items-center justify-center text-sm">
                PV
              </span>
              <span className="text-white/65 text-sm">
                © {new Date().getFullYear()} Mgr. Peter Vaško, PhD. — všetky práva vyhradené.
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-white/55">
              <a className="hover:text-[#C6F432] transition-colors" href={`mailto:${CONTACT.email}`}>
                {CONTACT.email}
              </a>
              <a className="hover:text-[#C6F432] transition-colors" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}>
                {CONTACT.phone}
              </a>
              <a
                className="hover:text-[#C6F432] transition-colors"
                href={CONTACT.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.08);
          color: #F5F5F5;
          padding: 14px 16px;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: all 0.25s ease;
          font-family: inherit;
        }
        .input::placeholder { color: rgba(245,245,245,0.32); }
        .input:focus {
          border-color: #C6F432;
          background: rgba(198,244,50,0.04);
          box-shadow: 0 0 0 4px rgba(198,244,50,0.08);
        }
      `}</style>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testId }) {
  return (
    <a
      data-testid={testId}
      href={href}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="group flex items-center justify-between gap-4 p-4 rounded-2xl border border-white/[0.07] bg-white/[0.02] hover:bg-[#C6F432]/[0.05] hover:border-[#C6F432]/35 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#0A0A0A] border border-white/10 flex items-center justify-center group-hover:border-[#C6F432]/40 transition-colors">
          <Icon className="w-5 h-5 text-[#C6F432]" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">
            {label}
          </div>
          <div className="text-base text-white font-medium">{value}</div>
        </div>
      </div>
      <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#C6F432] group-hover:rotate-12 transition-all" />
    </a>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-white/50 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
